/**
 * The resend-verification port (permissions `POST /v0/identity/verification-email`).
 *
 * A database signup gets one verification link, at signup. Before this port
 * nothing could send another, so a link that expired left the account
 * unverified for good — and accepting an invitation requires a verified email.
 *
 * The caller is never a parameter: the server mails the signed-in subject, for
 * the application their token was minted for. Speaks the canonical
 * `/verification-email` path, rewritten onto the `.management.` permissions
 * zone in `app/api-client.ts`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import { asNullableString, asRecord } from "./wire";

export type VerificationEmailOutcome =
  /** A new link is on its way to `email`. */
  | { status: "sent"; email: string | null }
  /** One went out moments ago (signup counts); it can be asked again after `retryAfterSeconds`. */
  | { status: "recently_sent"; email: string | null; retryAfterSeconds: number }
  /** Nothing to send: the address was verified after this session signed in. */
  | { status: "already_verified" };

export interface VerificationEmailSource {
  send(): Promise<VerificationEmailOutcome>;
}

export function outcomeFromWire(raw: unknown): VerificationEmailOutcome {
  const data = asRecord(raw);
  const email = asNullableString(data.email);
  switch (data.status) {
    case "already_verified":
      return { status: "already_verified" };
    case "recently_sent": {
      const seconds = Number(data.retry_after_seconds);
      return { status: "recently_sent", email, retryAfterSeconds: Number.isFinite(seconds) && seconds > 0 ? seconds : 0 };
    }
    case "sent":
      return { status: "sent", email };
    default:
      // An answer this port does not know is not proof that anything was sent.
      throw new Error("The verification email service gave an answer this page does not understand.");
  }
}

export function createVerificationEmailSource(client: ApiClient): VerificationEmailSource {
  return {
    async send(): Promise<VerificationEmailOutcome> {
      // Never retried: every send is an email in somebody's inbox, and a
      // timed-out request may well have sent one.
      return outcomeFromWire(unwrapData(await client.post<unknown>("/verification-email", undefined, { retries: 0 })));
    },
  };
}
